import type { DataResponse, SuccessResponse } from '@/api/types';
import { gateway } from '..';
import type { PlanVariantResource } from './resources/PlanVariantResource';
import type { SubscriptionStatusResource } from './resources/SubscriptionStatusResource';
import type { ContractSelfServiceResource } from './resources/ContractSelfServiceResource';
import type { PaymentGatewayResource } from './resources/PaymentGatewayResource';

type CheckoutResubscribeCommitResponseSuccess = DataResponse<{
    success: true;
    contractId: string;
    result: 'success';
}>;

type CheckoutResubscribeCommitResponseError = DataResponse<{
    success: false;
    contractId: null;
    result: string;
    force_update_payment_method: boolean;
}>;

export type UserContractSelfServiceResponse =
    | DataResponse<{
          success: false;
          message: string;
      }>
    | ContractSelfServiceResource;

export function getPaymentStatusBranching() {
    return gateway
        .get<
            DataResponse<
                | {
                      success: false;
                      message: string;
                  }
                | {
                      success: true;
                      status: SubscriptionStatusResource;
                      payment_gateways: PaymentGatewayResource[];
                  }
            >
        >('payments/subscription/status')
        .then((response) => response.data);
}

// BILLWERK ROUTES
export function getSubscriptionPlans() {
    return gateway
        .get<DataResponse<PlanVariantResource[]>>('payments/subscription/premium/plans/default/variants')
        .then((response) => response.data.data);
}

export function postSubscriptionSuccess(contractId: string) {
    return gateway
        .post<SuccessResponse>('payments/subscription/premium/checkout/success', { contract_id: contractId })
        .then((response) => response.data);
}

export function postSubscriptionCancel(contractId: string) {
    return gateway
        .post<SuccessResponse>('payments/subscription/premium/user/contract/end', { contract_id: contractId })
        .then((response) => response.data);
}

export function subscriptionCheckout(id: string) {
    return gateway
        .post<
            DataResponse<{
                url: string;
            }>
        >('payments/subscription/premium/checkout', { plan_version_id: id })
        .then((response) => response.data);
}

export function subscriptionResubscribeCreateOrder(id: string) {
    return gateway
        .post<
            DataResponse<{
                price: number;
                orderId: string;
                paymentMethod: {
                    type: string;
                };
            }>
        >('payments/subscription/premium/checkout/resubscribe/order', {
            plan_variant_id: id,
        })
        .then((response) => response.data.data);
}

export function subscriptionResubscribeCommit(orderId: string) {
    return gateway
        .post<CheckoutResubscribeCommitResponseSuccess | CheckoutResubscribeCommitResponseError>(
            'payments/subscription/premium/checkout/resubscribe/commit',
            {
                order_id: orderId,
            },
        )
        .then((response) => response.data.data);
}

export function getSelfServiceUrl() {
    return gateway
        .get<DataResponse<UserContractSelfServiceResponse>>('payments/subscription/premium/user/contract/self-service')
        .then((response) => response.data);
}

// CREDITS ROUTES
export function getSubscriptionCreditPlans() {
    return gateway
        .get<DataResponse<PlanVariantResource[]>>('payments/subscription/premium/credit/plans/default/variants')
        .then((response) => response.data.data);
}

export function subscriptionCreditCheckout(id: unknown, country: unknown) {
    return gateway
        .post<
            DataResponse<{
                session_id: string;
            }>
        >('payments/subscription/premium/credit/checkout', { plan_version_id: id, country })
        .then((response) => response.data);
}

export function postCreditSubscriptionSuccess(contractId: string) {
    return gateway
        .post<SuccessResponse>('payments/subscription/premium/credit/checkout/success', { contract_id: contractId })
        .then((response) => response.data);
}

// STRIPE ROUTES
export function getSubscriptionStripePlans() {
    const url = 'payments/subscription/premium/stripe/plans';

    return gateway.get<DataResponse<PlanVariantResource[]>>(url).then((response) => response.data.data);
}

export function postSubscriptionStripeSuccess(contractId: string) {
    return gateway
        .post<SuccessResponse>('payments/subscription/premium/stripe/checkout/success', { contract_id: contractId })
        .then((response) => response.data);
}

export function postSubscriptionStripeCancel() {
    return gateway
        .post<SuccessResponse>('payments/subscription/premium/stripe/end-contract')
        .then((response) => response.data);
}

export function subscriptionStripeCheckout(plan_variant_id: string, currency: string | undefined) {
    const data = currency ? { plan_variant_id, currency } : { plan_variant_id };
    return gateway
        .post<
            DataResponse<{
                url: string;
            }>
        >('/payments/subscription/premium/stripe/checkout', data)
        .then((response) => response.data);
}

export function getStripeSelfServiceUrl() {
    return gateway
        .get<DataResponse<UserContractSelfServiceResponse>>('payments/subscription/premium/stripe/self-service')
        .then((response) => response.data);
}

export function getStripeResubscribeUrl(plan_variant_id: string, currency: string | undefined) {
    const data = currency ? { plan_variant_id, currency } : { plan_variant_id };
    return gateway
        .post<
            DataResponse<UserContractSelfServiceResponse>
        >('payments/subscription/premium/stripe/checkout/resubscribe', data)
        .then((response) => response.data);
}

export function updateStripeSubscription() {
    return gateway.post<DataResponse<unknown>>('payments/subscription/premium/stripe/update-subscription');
}
